import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import {
  Components,
  Theme,
  alpha,
  outlinedInputClasses,
  toggleButtonClasses,
  toggleButtonGroupClasses,
} from "@mui/material";

import { brand, gray, sky } from "../themePrimitives";

// Pushes a button down beside a labelled field in a top-aligned row (one with helper text).
export const FIELD_LABEL_OFFSET = 28;

export const inputsCustomizations: Components<Theme> = {
  MuiButtonBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: "border-box",
        transition: "all 100ms ease-in",
        "&:focus-visible": {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: "2px",
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: theme.shape.borderRadius,
        textTransform: "none",
        height: "35px",
        maxHeight: "35px",
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              height: "35px",
              maxHeight: "35px",
              padding: "8px 12px",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              height: "35px",
              maxHeight: "35px",
            },
          },
          {
            props: {
              color: "primary",
              variant: "contained",
            },
            style: {
              color: "white",
              backgroundColor: gray[900],
              backgroundImage: `linear-gradient(to bottom, ${gray[700]}, ${gray[800]})`,
              boxShadow: "none",
              border: "none",
              "&:hover": {
                backgroundImage: "none",
                backgroundColor: gray[700],
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: gray[800],
              },
              "&.Mui-disabled": {
                color: gray[400],
                backgroundColor: gray[100],
                backgroundImage: "none",
                boxShadow: "none",
                border: `1px solid ${gray[200]}`,
              },
              ...theme.applyStyles("dark", {
                color: "#ffffff",
                backgroundColor: sky[400],
                backgroundImage: `linear-gradient(to bottom, ${sky[300]}, ${sky[400]})`,
                boxShadow: "none",
                border: "none",
                "&:hover": {
                  backgroundImage: "none",
                  backgroundColor: sky[300],
                  boxShadow: "none",
                },
                "&:active": {
                  backgroundColor: sky[500],
                },
                "&.Mui-disabled": {
                  color: gray[500],
                  backgroundColor: gray[600],
                  backgroundImage: "none",
                  boxShadow: "none",
                  border: `1px solid ${gray[600]}`,
                },
              }),
            },
          },
          {
            props: {
              color: "secondary",
              variant: "contained",
            },
            style: {
              color: "white",
              backgroundColor: brand[300],
              backgroundImage: `linear-gradient(to bottom, ${alpha(brand[400], 0.8)}, ${brand[500]})`,
              boxShadow: "none",
              border: "none",
              "&:hover": {
                backgroundColor: brand[700],
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: brand[700],
                backgroundImage: "none",
              },
              "&.Mui-disabled": {
                color: gray[400],
                backgroundColor: gray[100],
                backgroundImage: "none",
                boxShadow: "none",
                border: `1px solid ${gray[200]}`,
                ...theme.applyStyles("dark", {
                  color: gray[500],
                  backgroundColor: gray[600],
                  border: `1px solid ${gray[600]}`,
                }),
              },
              ...theme.applyStyles("dark", {
                backgroundColor: sky[300],
                backgroundImage: `linear-gradient(to bottom, ${alpha(sky[400], 0.8)}, ${sky[500]})`,
                boxShadow: "none",
                border: "none",
                "&:hover": {
                  backgroundColor: sky[700],
                  boxShadow: "none",
                },
                "&:active": {
                  backgroundColor: sky[700],
                  backgroundImage: "none",
                },
              }),
            },
          },
          {
            props: {
              variant: "outlined",
            },
            style: {
              color: theme.palette.text.primary,
              border: "1px solid",
              borderColor: gray[200],
              backgroundColor: alpha(gray[50], 0.3),
              "&:hover": {
                backgroundColor: gray[100],
                borderColor: gray[300],
              },
              "&:active": {
                backgroundColor: gray[200],
              },
              ...theme.applyStyles("dark", {
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.divider,

                "&:hover": {
                  backgroundColor: alpha(gray[100], 0.08),
                  borderColor: gray[500],
                },
                "&:active": {
                  backgroundColor: alpha(gray[100], 0.14),
                },
              }),
            },
          },
          {
            props: {
              color: "secondary",
              variant: "outlined",
            },
            style: {
              color: brand[700],
              border: "1px solid",
              borderColor: brand[200],
              backgroundColor: brand[50],
              "&:hover": {
                backgroundColor: brand[100],
                borderColor: brand[400],
              },
              "&:active": {
                backgroundColor: alpha(brand[200], 0.7),
              },
              ...theme.applyStyles("dark", {
                color: sky[50],
                border: "1px solid",
                borderColor: sky[900],
                backgroundColor: alpha(sky[900], 0.3),
                "&:hover": {
                  borderColor: sky[700],
                  backgroundColor: alpha(sky[900], 0.6),
                },
                "&:active": {
                  backgroundColor: alpha(sky[900], 0.5),
                },
              }),
            },
          },
          {
            props: {
              variant: "text",
            },
            style: {
              color: gray[600],
              "&:hover": {
                backgroundColor: gray[100],
              },
              "&:active": {
                backgroundColor: gray[200],
              },
              ...theme.applyStyles("dark", {
                color: gray[50],
                "&:hover": {
                  backgroundColor: alpha(gray[100], 0.08),
                },
                "&:active": {
                  backgroundColor: alpha(gray[100], 0.14),
                },
              }),
            },
          },
          {
            props: {
              color: "secondary",
              variant: "text",
            },
            style: {
              color: brand[700],
              "&:hover": {
                backgroundColor: alpha(brand[100], 0.5),
              },
              "&:active": {
                backgroundColor: alpha(brand[200], 0.7),
              },
              ...theme.applyStyles("dark", {
                color: sky[100],
                "&:hover": {
                  backgroundColor: alpha(sky[900], 0.5),
                },
                "&:active": {
                  backgroundColor: alpha(sky[900], 0.3),
                },
              }),
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: "10px",
        boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: brand[500],
        },
        ...theme.applyStyles("dark", {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: sky[300],
          },
          // boxShadow: `0 4px 16px ${alpha(sky[700], 0.5)}`,
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: "35px",
        maxHeight: "35px",
        padding: "0 16px",
        textTransform: "none",
        borderRadius: "10px",
        fontWeight: 500,
        ...theme.applyStyles("dark", {
          color: gray[400],
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
          [`&.${toggleButtonClasses.selected}`]: {
            color: sky[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      icon: <CheckBoxOutlineBlankRoundedIcon sx={{ color: "hsla(210, 0%, 0%, 0.0)" }} />,
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: "1px solid ",
        borderColor: alpha(gray[300], 0.8),
        boxShadow: "0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset",
        backgroundColor: alpha(gray[100], 0.4),
        transition: "border-color, background-color, 120ms ease-in",
        "&:hover": {
          borderColor: brand[300],
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: "2px",
          borderColor: brand[400],
        },
        "&.Mui-checked": {
          color: "white",
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          "&:hover": {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles("dark", {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: "0 0 0 1.5px hsl(210, 0%, 0%) inset",
          backgroundColor: alpha(gray[900], 0.8),
          "&:hover": {
            borderColor: sky[300],
          },
          "&.Mui-focusVisible": {
            borderColor: sky[400],
            outline: `3px solid ${alpha(sky[500], 0.5)}`,
            outlineOffset: "2px",
          },
          "&.Mui-checked": {
            backgroundColor: sky[500],
            borderColor: sky[500],
            "&:hover": {
              backgroundColor: sky[600],
            },
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: "none",
      },
      input: {
        "&::placeholder": {
          opacity: 0.7,
          color: gray[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      root: ({ theme }) => ({
        height: "35px",
        maxHeight: "35px",
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        color: theme.palette.text.primary,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        transition: "border 120ms ease-in",
        "&:hover": {
          borderColor: gray[400],
        },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          borderColor: brand[400],
        },
        [`&.${outlinedInputClasses.multiline}`]: {
          height: "auto",
          maxHeight: "none",
          display: "block",
          padding: "8px 12px",
        },
        ...theme.applyStyles("dark", {
          "&:hover": {
            borderColor: gray[500],
          },
          [`&.${outlinedInputClasses.focused}`]: {
            outline: `3px solid ${alpha(sky[500], 0.5)}`,
            borderColor: sky[400],
          },
        }),
      }),
      notchedOutline: {
        border: "none",
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        minHeight: "unset !important",
        display: "flex",
        alignItems: "center",
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.grey[500],
        ...theme.applyStyles("dark", {
          color: theme.palette.grey[400],
        }),
      }),
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      // Right padding keeps text and chips clear of the popup arrow (and the clear icon).
      inputRoot: {
        padding: "0 36px 0 12px !important",
      },
      endAdornment: {
        top: "50%",
        right: "6px !important",
        transform: "translateY(-50%)",
      },
      // Selected values of a multi-select: rounded squares with even spacing, not pills.
      root: ({ theme }) => ({
        "&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": {
          paddingRight: "62px !important",
        },
        // Chips centre on one line and wrap onto more lines instead of overflowing the fixed height.
        "& .MuiAutocomplete-inputRoot:has(.MuiAutocomplete-tag)": {
          height: "auto",
          maxHeight: "none",
          minHeight: 35,
          flexWrap: "wrap",
          alignItems: "center",
          paddingTop: "3px !important",
          paddingBottom: "3px !important",
          paddingLeft: "4px !important",
          gap: 4,
          "& .MuiAutocomplete-input": {
            minWidth: 80,
            height: 26,
            padding: "0 0 0 4px !important",
          },
        },
        "& .MuiAutocomplete-tag": {
          margin: 0,
          height: 26,
          borderRadius: 6,
          "& .MuiChip-label": {
            paddingInline: 8,
            fontWeight: 500,
            color: theme.palette.text.primary,
          },
          "& .MuiChip-deleteIcon": {
            fontSize: 16,
            margin: "0 6px 0 -2px",
            color: theme.palette.text.secondary,
            "&:hover": { color: theme.palette.text.primary },
          },
        },
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
      }),
    },
  },
  // A TextField's `label` sits above the input like FormField's: these inputs have no notch to float into.
  // FIELD_LABEL_OFFSET is that label's height with its margin.
  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: {
        position: "relative",
        transform: "none",
        maxWidth: "100%",
        transition: "none",
      },
    },
  },
};
